import * as React from "react";
import { Box } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import { CPHrBreak, CPText } from "shared/components/base";
import {
  useGetResourcesForRoleQuery,
  useGetUsersForRoleQuery,
  useUpsertRoleMutation,
} from "data/role/role";
import useAppState from "shared/state/useAppState";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import { Role, RoleResource, RoleUser } from "data/role/role";

export class RoleRecord implements Role {
  id: number = -1;
  name: string = "";
  externalSsoGroupId: string = "";
  usersIds: number[] = [];
  resources: string[] = [];
}

interface RoleDetailsModalProps {
  selectedRole: RoleRecord | null;
  onClose: () => void;
  onSave: () => void;
}

const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({
  selectedRole,
  onClose,
  onSave,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useUpsertRoleMutation();
  const { data: roleUsers, isLoading: roleUsersLoading } =
    useGetUsersForRoleQuery(selectedRole ? selectedRole.id : -1);

  const { data: roleResources, isLoading: roleResourcesLoading } =
    useGetResourcesForRoleQuery(selectedRole ? selectedRole.id : -1);

  const [editRole, setEditRole] = React.useState<RoleRecord>(new RoleRecord());
  const [userBoxOptions, setUserBoxOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [resourceBoxOptions, setResourceBoxOptions] =
    React.useState<CPBoxOption>({
      available: [],
      selected: [],
    });

  React.useEffect(() => {
    setEditRole(selectedRole ? { ...selectedRole } : new RoleRecord());
  }, [selectedRole]);

  React.useEffect(() => {
    setIsLoading(isSaving || roleUsersLoading || roleResourcesLoading);
  }, [isSaving, roleUsersLoading, roleResourcesLoading, setIsLoading]);

  React.useEffect(() => {
    if (roleUsers) {
      setUserBoxOptions({
        available: roleUsers.available.map((u: RoleUser) => ({
          value: u.id,
          label: `${u.firstname} ${u.lastname}`,
          selected: false,
        })),
        selected: roleUsers.assigned.map((u: RoleUser) => ({
          value: u.id,
          label: `${u.firstname} ${u.lastname}`,
          selected: false,
        })),
      });
    }
  }, [roleUsers]);

  React.useEffect(() => {
    if (roleResources) {
      setResourceBoxOptions({
        available: roleResources.available.map((r: RoleResource) => ({
          value: r.id,
          label: r.name,
          selected: false,
        })),
        selected: roleResources.assigned.map((r: RoleResource) => ({
          value: r.id,
          label: r.name,
          selected: false,
        })),
      });
    }
  }, [roleResources]);

  const handleSave = () => {
    mutate(
      {
        ...editRole,
        usersIds: userBoxOptions.selected.map((o) => o.value as number),

        resources: resourceBoxOptions.selected.map((o) => o.label),
      },
      {
        onSuccess: () => {
          setToastMsg("Role saved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }

          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const isNew = selectedRole?.id === -1;

  return (
    <CPActionsheetModal
      title={isNew ? "Add Role" : "Edit Role"}
      open={selectedRole !== null && !roleUsersLoading && !roleResourcesLoading}
      onClose={onClose}
      position="right"
      width={550}
      actionBtns={
        <Box spacing={8}>
          <CPButton themeColor="primary" onClick={handleSave}>
            Save
          </CPButton>
        </Box>
      }
    >
      {selectedRole && editRole && (
        <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                required
                name="name"
                label="Role Name"
                value={editRole.name}
                disabled={!isNew}
                error={saveError}
                onChange={(e) =>
                  setEditRole((prev) => ({
                    ...prev!,
                    name: e.target.value as string,
                  }))
                }
              />
            </Box>

            <Box className="cp-actionsheet-input-box">
              {" "}
              <CPTextBox
                name="externalSsoGroupId"
                label="SSO Group ID (Azure AD Object ID)"
                value={editRole.externalSsoGroupId ?? ""}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                onChange={(e) =>
                  setEditRole((prev) => ({
                    ...prev!,
                    externalSsoGroupId: e.target.value as string,
                  }))
                }
              />
            </Box>
          </Box>

          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                headerText="Assigned Resources"
                filterable
                boxOptions={resourceBoxOptions}
                onChange={setResourceBoxOptions}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                headerText="Assigned Users"
                filterable
                boxOptions={userBoxOptions}
                onChange={setUserBoxOptions}
              />
            </Box>
          </Box>
        </Box>
      )}
    </CPActionsheetModal>
  );
};

export default RoleDetailsModal;
